import React from "react";
import PropTypes from "prop-types";
import { Box, Typography, makeStyles } from "@material-ui/core";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import {
  Folder as FolderIcon,
  Description as ScenarioIcon,
  CheckCircle as DoneIcon,
  HourglassEmpty as RunningIcon,
  Error as ErrorIcon,
} from "@material-ui/icons";

const useStyles = makeStyles((theme) => ({
  // Folder card - horizontal style matching scenario cards
  folderCard: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    maxWidth: 280,
    minHeight: 72,
    border: "1px solid #e0e0e0",
    backgroundColor: "#f9f9f9",
    borderRadius: 8,
    cursor: "pointer",
    transition: "all 0.2s ease-in-out",
    padding: theme.spacing(1.25),
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
    position: "relative",
    "&:hover": {
      transform: "scale(1.02)",
      backgroundColor: "#ffffff",
      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
      borderColor: "#d0d0d0",
      borderLeft: (props) => `4px solid ${props.clientColor || "#1976d2"}`,
    },
  },
  folderSelected: {
    backgroundColor: "#ffffff",
    borderColor: "#d0d0d0",
    borderLeft: (props) => `4px solid ${props.clientColor || "#1976d2"}`,
    boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
    "&:hover": {
      backgroundColor: "#fafafa",
      borderLeftWidth: "5px",
      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    },
  },
  // Scenario card - horizontal style
  scenarioCard: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    maxWidth: 280,
    minHeight: 72,
    border: "1px solid #e0e0e0",
    backgroundColor: "#f9f9f9",
    borderRadius: 8,
    cursor: "pointer",
    transition: "all 0.2s ease-in-out",
    padding: theme.spacing(1.25),
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
    position: "relative",
    "&:hover": {
      transform: "scale(1.02)",
      backgroundColor: "#ffffff",
      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
      borderColor: "#d0d0d0",
      borderLeft: (props) => `4px solid ${props.clientColor || "#1976d2"}`,
    },
  },
  scenarioSelected: {
    backgroundColor: "#ffffff",
    borderColor: "#d0d0d0",
    borderLeft: (props) => `4px solid ${props.clientColor || "#1976d2"}`,
    boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
    "&:hover": {
      backgroundColor: "#fafafa",
      borderLeftWidth: "5px",
      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    },
  },
  dragging: {
    opacity: 0.8,
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
  },
  dropTarget: {
    backgroundColor: (props) => `${props.clientColor || "#1976d2"}1A`,
    border: (props) => `2px dashed ${props.clientColor || "#1976d2"}`,
  },
  // Folder icon wrapper (left aligned, horizontal)
  folderIconWrapper: {
    position: "relative",
    width: 44,
    height: 44,
    marginRight: theme.spacing(1.5),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  folderIcon: {
    fontSize: 36,
    opacity: 0.7,
    transition: "all 0.2s ease-in-out",
  },
  folderContent: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  folderName: {
    fontSize: "0.8rem",
    fontWeight: 500,
    lineHeight: 1.3,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: "#3C4858",
  },
  folderCount: {
    fontSize: "0.7rem",
    color: "#757575",
    lineHeight: 1.2,
  },
  // Scenario icon wrapper (left aligned)
  scenarioIconWrapper: {
    position: "relative",
    width: 44,
    height: 44,
    marginRight: theme.spacing(1.5),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  scenarioIcon: {
    fontSize: 36,
    opacity: 0.4,
    color: "#616161",
    transition: "all 0.2s ease-in-out",
  },
  scenarioContent: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  scenarioName: {
    fontSize: "0.8rem",
    fontWeight: 500,
    lineHeight: 1.3,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: "#3C4858",
  },
  scenarioMeta: {
    fontSize: "0.7rem",
    color: "#757575",
    lineHeight: 1.2,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  statusBadge: {
    width: 20,
    height: 20,
    borderRadius: 4,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "absolute",
    bottom: -2,
    right: -2,
    boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
  },
  statusDone: {
    backgroundColor: "#4caf50",
    color: "#ffffff",
  },
  statusRunning: {
    backgroundColor: "#ff9800",
    color: "#ffffff",
  },
  statusError: {
    backgroundColor: "#f44336",
    color: "#ffffff",
  },
  statusIcon: {
    fontSize: "0.75rem",
    color: "#ffffff",
  },
}));

const getStatusIcon = (status, classes) => {
  switch (status) {
    case "FINISHED":
      return (
        <Box className={`${classes.statusBadge} ${classes.statusDone}`}>
          <DoneIcon className={classes.statusIcon} />
        </Box>
      );
    case "PROCESSING":
    case "QUEUED":
    case "STARTED":
      return (
        <Box className={`${classes.statusBadge} ${classes.statusRunning}`}>
          <RunningIcon className={classes.statusIcon} />
        </Box>
      );
    case "ERROR":
      return (
        <Box className={`${classes.statusBadge} ${classes.statusError}`}>
          <ErrorIcon className={classes.statusIcon} />
        </Box>
      );
    default:
      return null;
  }
};

const formatDate = (timestamp) => {
  if (!timestamp) return "-";
  const date = new Date(timestamp);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const ContentGridItem = ({ type, data, isSelected, onClick, clientColor }) => {
  const classes = useStyles({ clientColor });
  const id =
    type === "folder"
      ? `folder-${data.folder_id}`
      : `scenario-${data.scenario_id}`;

  const {
    attributes,
    listeners,
    setNodeRef: setDragRef,
    isDragging,
  } = useDraggable({
    id,
    data: { type, ...data },
  });

  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id,
    data: { type, ...data },
    disabled: type !== "folder", // Only folders are drop targets
  });

  // Folder rendering
  if (type === "folder") {
    const cardClass = `${classes.folderCard} ${isSelected ? classes.folderSelected : ""} ${
      isDragging ? classes.dragging : ""
    } ${isOver ? classes.dropTarget : ""}`;

    return (
      <Box
        ref={(node) => {
          setDragRef(node);
          setDropRef(node);
        }}
        className={cardClass}
        onClick={onClick}
        {...attributes}
        {...listeners}
      >
        <Box className={classes.folderIconWrapper}>
          <FolderIcon
            className={classes.folderIcon}
            style={{ color: data.color || "#6B7280" }}
          />
        </Box>
        <Box className={classes.folderContent}>
          <Typography className={classes.folderName}>{data.name}</Typography>
          {data.scenario_count !== undefined && (
            <Typography className={classes.folderCount}>
              {data.scenario_count}{" "}
              {data.scenario_count === 1 ? "scenario" : "scenarios"}
            </Typography>
          )}
        </Box>
      </Box>
    );
  }

  // Scenario rendering - horizontal layout
  const cardClass = `${classes.scenarioCard} ${isSelected ? classes.scenarioSelected : ""} ${
    isDragging ? classes.dragging : ""
  }`;

  return (
    <Box
      ref={setDragRef}
      className={cardClass}
      onClick={onClick}
      {...attributes}
      {...listeners}
    >
      <Box className={classes.scenarioIconWrapper}>
        <ScenarioIcon className={classes.scenarioIcon} />
        {getStatusIcon(data.status, classes)}
      </Box>
      <Box className={classes.scenarioContent}>
        <Typography className={classes.scenarioName}>
          {data.scenario_name}
        </Typography>
        <Typography className={classes.scenarioMeta}>
          {data.created_by_user || "-"}
        </Typography>
        <Typography className={classes.scenarioMeta}>
          {formatDate(data.start_timestamp)}
        </Typography>
      </Box>
    </Box>
  );
};

ContentGridItem.propTypes = {
  type: PropTypes.oneOf(["folder", "scenario"]).isRequired,
  data: PropTypes.object.isRequired,
  isSelected: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  clientColor: PropTypes.string,
};

ContentGridItem.defaultProps = {
  isSelected: false,
  clientColor: "#1976d2",
};

export default ContentGridItem;
